/**
 * ComponentSlot — dispatch boundary for server-driven UI components.
 *
 * Dispatch order:
 * 1. Check delivery mode (lazy placeholder if no data yet)
 * 2. Check domain component registry (context-based, from ServerUiDomainProvider)
 * 3. Fall back to generic primitive renderer
 *
 * Domain components are registered by the consumer page (e.g., PatientPage)
 * via ServerUiDomainProvider. This keeps server-ui as pure infrastructure.
 */
import React from 'react';

import type { ServerUiComponent } from '../types';

import { LazyComponentLoader } from './LazyComponentLoader';
import { PrimitiveFallbackRenderer } from './PrimitiveFallbackRenderer';
import { useDomainComponents } from './ServerUiDomainProvider';

interface ComponentSlotProps {
  component: ServerUiComponent;
}

export const ComponentSlot: React.FC<ComponentSlotProps> = ({ component }) => {
  const domainComponents = useDomainComponents();

  // Lazy delivery — fetch on scroll visibility via LazyComponentLoader.
  // The `!component.data` check is intentional: lazy components that arrive
  // with data (because RTK Query hydrated them from cache after a previous
  // fetch) should flow through the normal dispatch path below, not re-enter
  // the loader. Don't "optimize" by removing the `!component.data` check.
  if (component.delivery === 'lazy' && !component.data) {
    return <LazyComponentLoader component={component} />;
  }

  if (!component.data) {
    console.warn(
      `[server-ui] ComponentSlot received null data for "${component.type_key}" (primitive: ${component.primitive})`,
    );
    return null;
  }

  // Try domain component dispatch first (type_key-based, from context)
  const DomainComponent = domainComponents[component.type_key];
  if (DomainComponent) {
    return <DomainComponent component={component} />;
  }

  // Fall back to generic primitive rendering
  return <PrimitiveFallbackRenderer component={component} />;
};
